import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppShell } from '@bravemove/ui-web';
import { useAuth } from '../auth/useAuth';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { NavCountsProvider } from '../navCounts/NavCountsProvider';
import { AdminTopNav } from './AdminTopNav';
import { AdminSideNav } from './AdminSideNav';
import { PageTitleContext } from './PageTitleContext';

/**
 * F06 — protected-area chrome. Wraps every authenticated route in:
 *
 *   <AppShell top={<AdminTopNav>} side={<AdminSideNav>}>
 *     <Outlet />
 *   </AppShell>
 *
 * The active page's title is lifted into the top bar (next to the ⓘ hint) via
 * `PageTitleContext`: `PageHeader` registers the title here and renders no body
 * title while inside the shell, so pages start at their content (less
 * whitespace). See components/PageHeader + layout/AdminTopNav.
 *
 * F60b — `NavCountsProvider` wraps the shell so the sidebar badges and the
 * dashboard share one visibility-aware `GET /admin/nav-counts` poll.
 *
 * NOTE: breadcrumbs are disabled for now (founder request). To restore them,
 * un-comment the `buildBreadcrumbs` block + imports at the bottom of this file
 * and the `<Breadcrumbs>` row in the JSX below.
 */
export function AdminShell() {
  const auth = useAuth();
  const location = useLocation();

  // ProtectedRoute guards the shell, so adminUser is always set at render.
  const adminUser = auth.adminUser!;

  // The active page's title (set by PageHeader) is surfaced in the top bar.
  // `setPageTitle` from useState is stable, so PageHeader's effect deps stay flat.
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const titleCtx = useMemo(() => ({ title: pageTitle, setTitle: setPageTitle }), [pageTitle]);

  return (
    <PageTitleContext.Provider value={titleCtx}>
      <NavCountsProvider>
        <AppShell
          top={<AdminTopNav adminUser={adminUser} />}
          side={<AdminSideNav permissions={adminUser.permissions} />}
        >
          <div className="p-6 space-y-6 max-w-[1400px] mx-auto w-full">
            {/* Breadcrumbs disabled for now (founder request):
            {crumbs.length > 1 ? <Breadcrumbs items={crumbs} /> : null} */}
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </AppShell>
      </NavCountsProvider>
    </PageTitleContext.Provider>
  );
}

/*
 * Breadcrumb builder — disabled for now (founder request); kept for easy
 * restore. To bring breadcrumbs back, re-add these imports at the top:
 *   import { useI18n } from '@bravemove/i18n/react';
 *   import { Breadcrumbs, type BreadcrumbItem } from '@bravemove/ui-web';
 *   import { OPERATIONS_NAV, PLATFORM_NAV } from './navItems';
 * then re-add `const { t } = useI18n();` + `const crumbs = buildBreadcrumbs(
 * location.pathname, t);` in AdminShell, plus the <Breadcrumbs> row above.
 *
 * type Translator = ReturnType<typeof useI18n>['t'];
 *
 * function buildBreadcrumbs(path: string, t: Translator): readonly BreadcrumbItem[] {
 *   const home: BreadcrumbItem = { label: t('admin:breadcrumbs.home'), href: '/' };
 *   const all = [...OPERATIONS_NAV, ...PLATFORM_NAV];
 *   const match = all.find((item) => item.route === path || path.startsWith(`${item.route}/`));
 *   if (!match || match.route === '/') return [home];
 *   return [home, { label: t(match.labelKey) }];
 * }
 */
